import { Button, Offcanvas } from 'react-bootstrap';
import ESpinner from 'components/others/Spinner';
import spinnerTitle from 'components/others/spinnerTitle';
import useCreateSupplierRequest from '../model/useCreateSupplierRequest';
import type { SupplierRequestCreateDraft } from '../model/types';
import RequestItemsTable from '../RequestItemsTable';
import CreateItemsAddBox from './CreateItemsAddBox';
import CreateNotesFields from './CreateNotesFields';
import CreateSendToVendor from './CreateSendToVendor';

interface OffCreateProps {
    vendorId: number | null;
    initialDraft?: SupplierRequestCreateDraft | null;
    cloneFromRequestId?: number | null;
    createKey?: number;
    onClose: () => void;
    onSaved: () => void;
}

const OffCreate = ({
    vendorId,
    initialDraft = null,
    cloneFromRequestId = null,
    createKey = 0,
    onClose,
    onSaved,
}: OffCreateProps) => {
    const show = vendorId != null;
    const {
        t,
        vendorId: activeVendorId,
        vendorName,
        sourceType,
        setSourceType,
        selectedProduct,
        setSelectedProduct,
        selectedSet,
        setSelectedSet,
        draftCount,
        setDraftCount,
        note,
        setNote,
        vendorNote,
        setVendorNote,
        handleDuplicateNoteToVendor,
        sendToVendor,
        setSendToVendor,
        vendorEmail,
        setVendorEmail,
        noteMaxLength,
        items,
        editCountIndex,
        setEditCountIndex,
        isAdding,
        isSaving,
        isLoadingDraft,
        handleDraftAdd,
        removeItem,
        saveCountEdit,
        handleSave,
        handleHide,
    } = useCreateSupplierRequest({
        vendorId,
        initialDraft,
        cloneFromRequestId,
        createKey,
        onClose,
        onSaved,
    });

    const busy = isSaving || isAdding || isLoadingDraft;

    return (
        <Offcanvas show={show} onHide={handleHide} placement="end" className="off-import">
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>
                    {t('supplierRequests.create.title', {
                        vendor: vendorName || `#${activeVendorId || ''}`,
                    })}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {isLoadingDraft ? (
                    <ESpinner />
                ) : (
                    <div className="off-coming-form d-flex flex-column h-100">
                        <div className="off-coming-content">
                            <CreateItemsAddBox
                                vendorId={activeVendorId}
                                sourceType={sourceType}
                                setSourceType={setSourceType}
                                selectedProduct={selectedProduct}
                                setSelectedProduct={setSelectedProduct}
                                selectedSet={selectedSet}
                                setSelectedSet={setSelectedSet}
                                draftCount={draftCount}
                                setDraftCount={setDraftCount}
                                isAdding={isAdding}
                                busy={busy}
                                onAdd={handleDraftAdd}
                            />

                            <div className="mx-3 mb-3">
                                <RequestItemsTable
                                    items={items}
                                    editCountIndex={editCountIndex}
                                    setEditCountIndex={setEditCountIndex}
                                    onSaveCount={saveCountEdit}
                                    onRemove={removeItem}
                                    disabled={busy}
                                    showActions
                                    showReceived={false}
                                />
                            </div>

                            <CreateNotesFields
                                note={note}
                                setNote={setNote}
                                vendorNote={vendorNote}
                                setVendorNote={setVendorNote}
                                noteMaxLength={noteMaxLength}
                                busy={busy}
                                show={show}
                                vendorId={vendorId}
                                createKey={createKey}
                                isLoadingDraft={isLoadingDraft}
                                onDuplicateNoteToVendor={handleDuplicateNoteToVendor}
                            />

                            <CreateSendToVendor
                                sendToVendor={sendToVendor}
                                setSendToVendor={setSendToVendor}
                                vendorEmail={vendorEmail}
                                setVendorEmail={setVendorEmail}
                                busy={busy}
                            />
                        </div>

                        <div className="p-3 offcanvas-footer off-coming-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" disabled={busy} onClick={handleHide}>
                                    {t('common:buttons.cancel')}
                                </Button>
                                <Button
                                    variant="success"
                                    className="min100px"
                                    disabled={busy || !items.length}
                                    onClick={() => void handleSave()}
                                >
                                    {spinnerTitle(
                                        t('supplierRequests.create.btnSave'),
                                        isSaving,
                                        t('common:buttons.saving'),
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffCreate;
